'use client';

import React from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from './button';
import { TechnicalValue } from './technical-value';
import { buttonVariants } from './button-variants';
import { cn } from '../lib/utils';

export interface ErrorStateProps {
  title?: string;
  message?: string;
  onRetry?: () => void;
  retryLabel?: string;
  className?: string;
  /** Safe support identifier supplied by the error boundary; never a raw error message. */
  referenceCode?: string;
}

export function ErrorState({
  title,
  message,
  onRetry,
  className,
  referenceCode,
  retryLabel = 'Try again',
}: ErrorStateProps) {
  const fallbackReference = React.useId();
  const reference = referenceCode ?? `UI-${fallbackReference.replace(/[^a-z0-9]/gi, '')}`;
  return (
    <div
      className={cn(
        'flex w-full max-w-2xl flex-col items-start gap-2 rounded-control border border-status-danger-border bg-status-danger-bg p-4 text-left',
        className,
      )}
      role="alert"
    >
      <div className="flex size-6 items-center justify-center">
        <AlertCircle className="size-5 text-status-danger-fg" aria-hidden="true" />
      </div>
      {title && (
        <p className="font-body text-card-title font-semibold text-text-heading">{title}</p>
      )}
      {message && (
        <p className={cn('max-w-2xl font-body text-body text-text-body', 'mt-0')}>{message}</p>
      )}
      <p className="flex items-center gap-2 font-body text-caption text-text-muted">
        Reference: <TechnicalValue label="Support reference" value={reference} />
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {onRetry && (
          <Button type="button" variant="secondary" onClick={onRetry}>
            {retryLabel}
          </Button>
        )}
        <a href="/help" className={buttonVariants({ variant: 'link' })}>
          Help
        </a>
      </div>
    </div>
  );
}
